import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { getAuditLogs } from "../../services/playgroundService";
import "./Playground.css";

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
};

const formatValue = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "string") return val === "" ? "—" : val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

// Normalize field names to readable format
const normalizeFieldName = (fieldPath) => {
  if (!fieldPath || fieldPath === "—") return "—";
  
  // Remove fouray_response.response prefix if present
  let path = fieldPath.replace(/^fouray_response\.response\.?/, "");
  path = path.replace(/^response\.?/, "");
  
  // Split by dots to handle nested paths
  const parts = path.split(".");
  
  // Normalize each part
  const normalizedParts = parts.map((part, index) => {
    // Handle array indices (numeric parts)
    if (part.match(/^\d+$/)) {
      return `Item ${parseInt(part) + 1}`;
    }
    
    // Remove array bracket notation if present
    part = part.replace(/\[\d+\]$/, "");
    
    // Convert snake_case to Title Case
    const normalized = part
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    
    return normalized;
  });
  
  return normalizedParts.join(" → ");
};

const flattenChanges = (node, prefix = "") => {
  if (!node || typeof node !== "object") return [];

  const rows = [];
  for (const [key, value] of Object.entries(node)) {
    // Skip fouray_response prefix
    if (key === "fouray_response" && value && typeof value === "object") {
      // Process all keys in fouray_response
      for (const [subKey, subValue] of Object.entries(value)) {
        if (subKey === "response" && subValue && typeof subValue === "object") {
          // Process response object directly without prefix
          rows.push(...flattenChanges(subValue, ""));
        } else if (subValue && typeof subValue === "object" && ("from" in subValue || "to" in subValue)) {
          // Handle direct changes like agent_id, session_id
          rows.push({
            path: subKey,
            operation: "modified",
            from: subValue.from,
            to: subValue.to,
          });
        } else if (subValue !== null && subValue !== undefined) {
          // Handle other fields
          rows.push(...flattenChanges({ [subKey]: subValue }, ""));
        }
      }
      continue;
    }

    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      // Leaf format: {operation, path, from/to/value}
      if ("operation" in value && ("path" in value || "from" in value || "to" in value || "value" in value)) {
        rows.push({
          path: value.path || currentPath,
          operation: value.operation || "modified",
          from: value.from,
          to: value.to,
          value: value.value,
        });
        continue;
      }

      // Leaf format: {from,to}
      if ("from" in value || "to" in value) {
        rows.push({
          path: currentPath,
          operation: "modified",
          from: value.from,
          to: value.to,
        });
        continue;
      }

      rows.push(...flattenChanges(value, currentPath));
      continue;
    }

    // Handle array changes (like line_items)
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item && typeof item === "object") {
          // Skip empty objects
          if (Object.keys(item).length === 0) {
            return;
          }
          
          // Check if this is an object with field changes (like {total: {from: X, to: Y}})
          const hasFieldChanges = Object.values(item).some(
            (v) => v && typeof v === "object" && ("from" in v || "to" in v)
          );
          
          if (hasFieldChanges) {
            // Process each field in the array item
            Object.entries(item).forEach(([fieldKey, fieldValue]) => {
              if (fieldValue && typeof fieldValue === "object" && ("from" in fieldValue || "to" in fieldValue)) {
                rows.push({
                  path: `${currentPath}.${index}.${fieldKey}`,
                  operation: "modified",
                  from: fieldValue.from,
                  to: fieldValue.to,
                });
              }
            });
          } else if ("from" in item || "to" in item) {
            // Item itself is a change entry
            rows.push({
              path: currentPath,
              operation: "modified",
              from: item.from,
              to: item.to,
            });
          } else {
            // Recursively process nested objects in array
            rows.push(...flattenChanges(item, `${currentPath}.${index}`));
          }
        }
      });
      continue;
    }

    // Primitive leaf
    rows.push({
      path: currentPath,
      operation: "modified",
      from: value,
      to: value,
    });
  }

  return rows;
};

const AuditLogModal = ({ onClose, tableName, recordId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!tableName || !recordId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getAuditLogs(tableName, recordId);
        setLogs(res.data?.data || []);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Failed to load audit logs");
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [tableName, recordId]);

  const rows = useMemo(() => {
    const flattened = [];
    for (const log of logs || []) {
      const changesRows = flattenChanges(log.changes || {});
      if (changesRows.length === 0) {
        flattened.push({
          auditId: log.id,
          action: log.action,
          path: "—",
          operation: "—",
          from: undefined,
          to: undefined,
          value: undefined,
          user: log.user,
          created_at: log.created_at,
        });
      } else {
        for (const r of changesRows) {
          flattened.push({
            auditId: log.id,
            action: log.action,
            ...r,
            user: log.user,
            created_at: log.created_at,
          });
        }
      }
    }

    // newest first
    flattened.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return flattened;
  }, [logs]);

  return (
    <div className="audit-log-overlay" onClick={onClose}>
      <div className="audit-log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="audit-log-header">
          <h2 className="audit-log-title">Audit Log</h2>
          <button className="audit-log-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="audit-log-content">
          {loading ? (
            <div className="playground-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading audit logs...</p>
            </div>
          ) : error ? (
            <div className="playground-error">
              <p>{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="dynamic-content-empty">
              <p>No audit logs found</p>
            </div>
          ) : (
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  <th>Updated By</th>
                  <th>Updated At</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const op = r.operation || "modified";
                  const rawPath = r.path || "—";
                  const fieldName = normalizeFieldName(rawPath);
                  const userLabel = r.user?.name || r.user?.email || "—";

                  const oldValue =
                    op === "added"
                      ? "—"
                      : formatValue(
                          r.value !== undefined ? r.value : r.from
                        );

                  const newValue =
                    op === "removed"
                      ? "—"
                      : formatValue(
                          r.value !== undefined ? r.value : r.to
                        );

                  return (
                    <tr key={`${r.auditId}-${idx}`}>
                      <td>{fieldName}</td>
                      <td>{oldValue}</td>
                      <td className="new-value-cell">{newValue}</td>
                      <td>{userLabel}</td>
                      <td>{formatDateTime(r.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogModal;

